import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Package, ShoppingCart, LogOut, TrendingUp, Clock, CheckCircle, Truck, IndianRupee } from 'lucide-react';
import { AuthContext, API } from '../../App';
import { toast } from 'sonner';

const AdminLayout = ({ children, activeTab }) => {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' },
    { id: 'products', label: 'Products', icon: Package, path: '/admin/products' },
    { id: 'orders', label: 'Orders', icon: ShoppingCart, path: '/admin/orders' },
  ];

  return (
    <div className="min-h-screen bg-[#F4F4F4]" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#1A1A1A] text-white">
        <div className="p-6 border-b border-white/10">
          <Link to="/">
            <span className="font-heading text-xl font-semibold tracking-tight text-white">in-dekor™</span>
          </Link>
          <p className="font-body text-xs text-gray-400 mt-2">Admin Panel</p>
        </div>
        
        <nav className="p-4">
          {navItems.map((item) => (
            <Link
              key={item.id}
              to={item.path}
              className={`flex items-center px-4 py-3 mb-1 font-body text-sm transition-colors ${
                activeTab === item.id
                  ? 'bg-[#C4A882] text-white'
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
              data-testid={`admin-nav-${item.id}`}
            >
              <item.icon className="w-5 h-5 mr-3" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-4 py-3 font-body text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        {children}
      </main>
    </div>
  );
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch stats');
      
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  const statCards = stats ? [
    { label: 'Total Products', value: stats.total_products, icon: Package, color: 'bg-blue-500' },
    { label: 'Total Orders', value: stats.total_orders, icon: ShoppingCart, color: 'bg-green-500' },
    { label: 'Pending Orders', value: stats.pending_orders, icon: Clock, color: 'bg-yellow-500' },
    { label: 'Total Revenue', value: `₹${stats.total_revenue.toLocaleString('en-IN')}`, icon: IndianRupee, color: 'bg-[#C4A882]' },
  ] : [];

  const orderStatusCards = stats ? [
    { label: 'Pending', value: stats.pending_orders, icon: Clock, color: 'text-yellow-500' },
    { label: 'Confirmed', value: stats.confirmed_orders, icon: CheckCircle, color: 'text-blue-500' },
    { label: 'Delivered', value: stats.delivered_orders, icon: Truck, color: 'text-green-500' },
  ] : [];

  return (
    <AdminLayout activeTab="dashboard">
      <div data-testid="admin-dashboard">
        <h1 className="font-heading text-2xl font-semibold text-[#1A1A1A] mb-8">Dashboard</h1>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white p-6 animate-pulse">
                <div className="h-4 bg-gray-200 w-1/2 mb-2"></div>
                <div className="h-8 bg-gray-200 w-1/3"></div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {statCards.map((stat, index) => (
                <div key={index} className="bg-white p-6" data-testid={`stat-card-${index}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-body text-xs uppercase tracking-wider text-[#666666]">{stat.label}</p>
                      <p className="font-heading text-2xl font-bold text-[#1A1A1A] mt-1">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 ${stat.color} flex items-center justify-center`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Status */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <div className="bg-white p-6">
                <h2 className="font-heading text-lg font-semibold text-[#1A1A1A] mb-4">Order Status</h2>
                <div className="grid grid-cols-3 gap-4">
                  {orderStatusCards.map((status, index) => (
                    <div key={index} className="text-center p-4 bg-[#F9F9F8]">
                      <status.icon className={`w-8 h-8 mx-auto mb-2 ${status.color}`} />
                      <p className="font-heading text-xl font-bold text-[#1A1A1A]">{status.value}</p>
                      <p className="font-body text-xs text-[#666666]">{status.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Orders */}
              <div className="bg-white p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="font-heading text-lg font-semibold text-[#1A1A1A]">Recent Orders</h2>
                  <Link to="/admin/orders" className="font-body text-sm text-[#C4A882] hover:underline">
                    View All
                  </Link>
                </div>
                <div className="space-y-3">
                  {stats.recent_orders.length === 0 ? (
                    <p className="font-body text-sm text-[#666666] text-center py-8">No orders yet</p>
                  ) : (
                    stats.recent_orders.map((order) => (
                      <div key={order.id} className="flex items-center justify-between py-3 border-b border-[#E5E5E5] last:border-0">
                        <div>
                          <p className="font-body text-sm font-medium text-[#1A1A1A]">{order.order_id}</p>
                          <p className="font-body text-xs text-[#666666]">{order.customer_name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-heading text-sm font-semibold text-[#1A1A1A]">
                            ₹{order.total.toLocaleString('en-IN')}
                          </p>
                          <span className={`inline-block px-2 py-0.5 text-xs ${
                            order.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                            order.status === 'Confirmed' ? 'bg-blue-100 text-blue-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {order.status}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white p-6">
              <h2 className="font-heading text-lg font-semibold text-[#1A1A1A] mb-4">Quick Actions</h2>
              <div className="flex gap-4">
                <Link to="/admin/products" className="btn-primary text-sm" data-testid="quick-add-product">
                  Add New Product
                </Link>
                <Link to="/admin/orders" className="btn-secondary text-sm" data-testid="quick-view-orders">
                  View All Orders
                </Link>
                <Link to="/" className="btn-secondary text-sm" data-testid="quick-view-store">
                  View Store
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export { AdminLayout };
export default AdminDashboard;
